import React, { useState, useEffect, useCallback, useRef } from 'react';
import { NavLink, Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import 'bootstrap-icons/font/bootstrap-icons.css';
import 'datatables.net-bs4';
import 'datatables.net-bs4/css/dataTables.bootstrap4.min.css';
import Loader from "../components/Loader";
import Swal from "sweetalert2";
import axios from "axios";
import Skeleton from 'react-loading-skeleton';
import ImageNotFound from '../assets/images/not-found.png';

const MainLayout = () => {
    const [darkMode, setDarkMode] = useState(false);
    const [sidebarOpen, setSidebarOpen] = useState(true);
    const [activeSubmenu, setActiveSubmenu] = useState(null);
    const [profileMenuOpen, setProfileMenuOpen] = useState(false);
    const [notificationMenuOpen, setNotificationMenuOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [name, setName] = useState('');
    const [last_name, setLastName] = useState('');
    const [rol, setRol] = useState('');
    const [avatar, setAvatar] = useState('');
    const [year, setYear] = useState(0);
    const [menuItems, setMenuItems] = useState([]);
    const [company, setCompany] = useState([]);
    const [loadingLogo, setLoadingLogo] = useState(true);
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [userRolId, setUserRolId] = useState(null); // Nuevo estado para almacenar el rol_id
    const location = useLocation();
    const navigate = useNavigate();
    const contentRef = useRef(null);
    const sidebarRef = useRef(null);
    const notificationRef = useRef(null);
    const URLZFILES = process.env.REACT_APP_API_ZFILES;
    const URLWEB = process.env.REACT_APP_WEB;
    const ENDPOINT = process.env.REACT_APP_URL_ENDPOINT;

    // Función para verificar si el usuario debe ver las notificaciones
    const shouldShowNotifications = useCallback(() => {
        return userRolId !== 8 && userRolId !== 9;
    }, [userRolId]);

    const toggleDarkMode = useCallback(() => setDarkMode(prev => !prev), []);
    const toggleSidebar = useCallback(() => {
        setSidebarOpen(prev => !prev);
    }, []);
    const toggleSubmenu = useCallback((index) => {
        setActiveSubmenu(prevActiveSubmenu => {
            const newActiveSubmenu = prevActiveSubmenu === index ? null : index;
            localStorage.setItem('activeSubmenu', newActiveSubmenu);
            return newActiveSubmenu;
        });
    }, []);
    const toggleProfileMenu = useCallback(() => setProfileMenuOpen(prev => !prev), []);
    const toggleNotificationMenu = useCallback(() => {
        setNotificationMenuOpen(prev => {
            if (!prev) {
                markAllAsRead();
            }
            return !prev;
        });
    }, []);
    const closeProfileMenu = useCallback(() => setProfileMenuOpen(false), []);
    const closeNotificationMenu = useCallback(() => setNotificationMenuOpen(false), []);

    const closeSidebar = useCallback(() => {
        if (window.innerWidth < 992) {
            setSidebarOpen(false);
        }
    }, []);

    const loadMenuItems = async () => {
        try {
            setLoading(true);
            const user = JSON.parse(localStorage.getItem('dataUser'));
            const rol_id = user.rol_id;
            const form = new FormData();
            form.append('rol_id', rol_id);
            const response = await axios.post(ENDPOINT + 'get-permissions-by-rol_id', form);
            if (response.data.res) {
                setLoading(false);
                setMenuItems(response.data.data);
            }
        } catch (error) {
            console.error('Error loading menu:', error);
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'No se pudo cargar el menú'
            });
        } finally {
            setLoading(false);
        }
    };

    const getKilometrageNotifications = useCallback(async () => {
        try {
            const response = await axios.get(ENDPOINT + 'get-kilometrage-notifications');
            if (response.data.res) {
                return response.data.data.map(notif => ({
                    ...notif,
                    type: 'kilometrage',
                    id: notif.kilometrage_id
                }));
            }
            return [];
        } catch (error) {
            console.error('Error getting kilometrage notifications:', error);
            return [];
        }
    }, [ENDPOINT]);

    const getPoliciesNotifications = useCallback(async () => {
        try {
            // Usamos el mismo endpoint que en InsurancePolicies.js
            const form = new FormData();
            form.append("status_filter", 2); // Todos
            form.append("unit_filter", 0);   // Todas las unidades
            
            const response = await axios.post(ENDPOINT + "get-all-policies", form);
            
            if (response.data.res && response.data.data) {
                const today = new Date();
                const thirtyDaysFromNow = new Date();
                thirtyDaysFromNow.setDate(today.getDate() + 8);
                
                // Filtrar pólizas que están por vencer (en los próximos 30 días)
                const expiringPolicies = response.data.data.filter(policy => {
                    const expDate = new Date(policy.expiration_date);
                    return expDate > today && expDate <= thirtyDaysFromNow;
                });
                
                console.log('Pólizas por vencer encontradas:', expiringPolicies.length);
                
                return expiringPolicies.map(policy => ({
                    policy_id: policy.policy_id,
                    nickname: policy.nickname,
                    unit_name: policy.unit?.name || 'Unidad no asignada',
                    expiration_date: policy.expiration_date,
                    days_remaining: Math.ceil((new Date(policy.expiration_date) - today) / (1000 * 60 * 60 * 24)),
                    status: 'Por vencer',
                    date: new Date(policy.expiration_date).toISOString().split('T')[0],
                    read: false,
                    type: 'policy',
                    id: policy.policy_id
                }));
            }
            return [];
        } catch (error) {
            console.error('Error getting policies notifications:', error);
            return [];
        }
    }, [ENDPOINT]);

    const getNotifications = useCallback(async () => {
        // Si el usuario no debe ver notificaciones, no hacer nada
        if (!shouldShowNotifications()) {
            setNotifications([]);
            setUnreadCount(0);
            return;
        }

        try {
            console.log('Obteniendo notificaciones...');
            const [kilometrageNotifications, policiesNotifications] = await Promise.all([
                getKilometrageNotifications(),
                getPoliciesNotifications()
            ]);

            console.log('Notificaciones de kilometraje:', kilometrageNotifications.length);
            console.log('Notificaciones de pólizas:', policiesNotifications.length);

            // Combinar y ordenar por fecha (más recientes primero)
            const allNotifications = [...kilometrageNotifications, ...policiesNotifications]
                .sort((a, b) => new Date(b.date) - new Date(a.date));
            
            setNotifications(allNotifications);
            
            const unread = allNotifications.filter(notif => !notif.read).length;
            setUnreadCount(unread);
            
            console.log('Total de notificaciones:', allNotifications.length);
            console.log('No leídas:', unread);
        } catch (error) {
            console.error('Error getting all notifications:', error);
            setNotifications([]);
            setUnreadCount(0);
        }
    }, [getKilometrageNotifications, getPoliciesNotifications, shouldShowNotifications]);

    const markAllAsRead = async () => {
        try {
            // Marcar todas las notificaciones como leídas en el frontend
            setNotifications(prev => prev.map(notif => ({ ...notif, read: true })));
            setUnreadCount(0);
            
            // Intentar marcar como leídas en el backend si los endpoints existen
            try {
                await axios.post(ENDPOINT + 'mark-all-kilometrage-notifications-read');
            } catch (error) {
                console.log('No se pudo marcar notificaciones de kilometraje como leídas:', error);
            }
        } catch (error) {
            console.error('Error marking all notifications as read:', error);
        }
    };

    const markAsRead = async (id, type) => {
        try {
            // Marcar como leída en el frontend
            setNotifications(prev => prev.map(notif => 
                notif.id === id && notif.type === type ? { ...notif, read: true } : notif
            ));
            setUnreadCount(prev => prev - 1);
            
            // Navegar a la sección correspondiente
            if (type === 'kilometrage') {
                navigate('/kilometrages');
            } else if (type === 'policy') {
                navigate('/polizas-seguro');
            }
            
            // Intentar marcar como leída en el backend para kilometraje
            if (type === 'kilometrage') {
                try {
                    const formData = new FormData();
                    formData.append('notification_id', id);
                    await axios.post(ENDPOINT + 'mark-kilometrage-notification-read', formData);
                } catch (backendError) {
                    console.log('No se pudo marcar la notificación de kilometraje como leída en el backend:', backendError);
                }
            }
            // Para pólizas, solo marcamos en frontend por ahora
        } catch (error) {
            console.error('Error marking notification as read:', error);
        }
    };

    useEffect(() => {
        const user = JSON.parse(localStorage.getItem('dataUser'));
        if (!user) {
            navigate('/');
        } else {
            setName(user.name);
            setLastName(user.last_name);
            setRol(user.rol.name);
            setAvatar(user.avatar);
            setUserRolId(user.rol_id); // Guardar el rol_id del usuario
            const current = new Date();
            const year = current.getFullYear();
            setYear(year);
            loadMenuItems();
            getInfoCompany();
        }
    }, []);

    useEffect(() => {
        const storedActiveSubmenu = localStorage.getItem('activeSubmenu');
        if (storedActiveSubmenu) {
            setActiveSubmenu(parseInt(storedActiveSubmenu));
        }

        const handleResize = () => {
            if (window.innerWidth >= 992) {
                setSidebarOpen(true);
            }
        };

        const handleClickOutside = (event) => {
            if (sidebarRef.current && !sidebarRef.current.contains(event.target) && window.innerWidth < 992) {
                closeSidebar();
            }
            if (notificationMenuOpen && notificationRef.current && !notificationRef.current.contains(event.target) && !event.target.closest('.notification-toggle')) {
                closeNotificationMenu();
            }
            if (profileMenuOpen && !event.target.closest('.profile-toggle') && !event.target.closest('.profile-dropdown')) {
                closeProfileMenu();
            }
        };

        window.addEventListener('resize', handleResize);
        document.addEventListener('mousedown', handleClickOutside);
        handleResize();

        return () => {
            window.removeEventListener('resize', handleResize);
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [closeSidebar, notificationMenuOpen, closeNotificationMenu, profileMenuOpen, closeProfileMenu]);

    useEffect(() => {
        document.body.classList.toggle('dark-mode', darkMode);
    }, [darkMode]);

    useEffect(() => {
        if (menuItems.length > 0 && shouldShowNotifications()) {
            getNotifications();
            const interval = setInterval(getNotifications, 300000); // Actualizar cada 5 minutos
            return () => clearInterval(interval);
        }
    }, [menuItems, getNotifications, shouldShowNotifications]);

    const closeSession = () => {
        Swal.fire({
            title: `¡Hasta pronto ${name} ${last_name}!`,
            text: "¿Deseas cerrar tu sesión actual?",
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: '<i class="bi bi-box-arrow-right me-2"></i>Sí, cerrar sesión',
            cancelButtonText: '<i class="bi bi-x-circle me-2"></i>No, continuar trabajando',
            allowOutsideClick: false,
            allowEscapeKey: false
        }).then((result) => {
            if (result.isConfirmed) {
                Swal.fire({
                    title: '<i class="bi bi-clock-history me-2"></i>Cerrando sesión',
                    html: 'Por favor espere...',
                    allowOutsideClick: false,
                    allowEscapeKey: false,
                    didOpen: () => {
                        Swal.showLoading()
                    }
                });

                setTimeout(() => {
                    localStorage.removeItem('dataUser');
                    Swal.fire({
                        icon: 'success',
                        title: '<i class="bi bi-check-circle-fill me-2"></i>¡Sesión finalizada!',
                        text: 'Has cerrado sesión correctamente.',
                        showConfirmButton: false,
                        timer: 1500
                    }).then(() => {
                        navigate('/');
                    });
                }, 1000);
            }
        });
    }

    const getInfoCompany = async () => {
        await axios.get(ENDPOINT + 'get-company/' + 1)
            .then(response => {
                setCompany(response.data.data);
                setLoadingLogo(false);
            }).catch(function (error) {
                console.log(error);
                setLoadingLogo(false);
            });
    };

    const MenuItem = ({ item, index }) => {
        const isActive = location.pathname === item.link || (item.submenu && item.submenu.some(subItem => location.pathname === subItem.link));
        const isSubmenuOpen = activeSubmenu === index;

        const handleItemClick = () => {
            if (item.submenu && item.submenu.length > 0) {
                toggleSubmenu(index);
            } else if (item.link) {
                navigate(item.link);
                closeSidebar();
            }
        };

        return (
            <div className={`sidebar-menu-item ${isActive ? 'active' : ''}`}>
                <button
                    onClick={handleItemClick}
                    className={`sidebar-menu-button ${isActive ? 'active' : ''}`}
                    aria-expanded={isSubmenuOpen}
                    style={{ cursor: item.submenu && item.submenu.length > 0 ? 'pointer' : (item.link ? 'pointer' : 'default') }}
                >
                    <span className="sidebar-menu-icon">
                        <i className={`bi ${item.icon}`}></i>
                    </span>
                    <span className="sidebar-menu-text">{item.text}</span>
                    {item.submenu && item.submenu.length > 0 && (
                        <i className={`bi ${isSubmenuOpen ? 'bi-chevron-up' : 'bi-chevron-down'}`}></i>
                    )}
                </button>
                {item.submenu && item.submenu.length > 0 && isSubmenuOpen && (
                    <div className="sidebar-submenu">
                        {item.submenu.map((subItem, subIndex) => (
                            <NavLink
                                key={subIndex}
                                to={subItem.link}
                                className={({ isActive }) => `sidebar-submenu-item ${isActive ? 'active' : ''}`}
                                onClick={closeSidebar}
                            >
                                <i className={`bi ${subItem.icon} sidebar-submenu-icon`}></i>
                                <span className="sidebar-submenu-text">{subItem.text}</span>
                            </NavLink>
                        ))}
                    </div>
                )}
            </div>
        );
    };

    const getNotificationIcon = (notification) => {
        if (notification.type === 'kilometrage') {
            return notification.status === 'Pasado o En Km' 
                ? 'bi-exclamation-triangle-fill text-danger' 
                : 'bi-info-circle-fill text-warning';
        } else if (notification.type === 'policy') {
            return 'bi-file-earmark-medical-fill text-warning';
        }
        return 'bi-bell-fill text-info';
    };

    const getNotificationTitle = (notification) => {
        if (notification.type === 'kilometrage') {
            return notification.unit_name;
        } else if (notification.type === 'policy') {
            return notification.nickname;
        }
        return 'Notificación';
    };

    const getNotificationText = (notification) => {
        if (notification.type === 'kilometrage') {
            return notification.status === 'Pasado o En Km' 
                ? `¡Servicio requerido! (Servicio a Realizar: ${notification.title}, Se realizo: ${notification.current_km} km + ${notification.estimated_km} km estimados = ${notification.total_estimated} km)`
                : `Servicio próximo: Servicio a Realizar: ${notification.title}, Se realizo: ${notification.next_service_km} km (${notification.total_estimated} km estimados)`;
        } else if (notification.type === 'policy') {
            const expDate = new Date(notification.expiration_date).toLocaleDateString();
            const daysText = notification.days_remaining === 1 ? 'día' : 'días';
            return `Póliza por vencer: ${notification.unit_name} - Vence el ${expDate} (${notification.days_remaining} ${daysText} restantes)`;
        }
        return 'Nueva notificación';
    };

    const handleNotificationClick = (notification) => {
        markAsRead(notification.id, notification.type);
    };

    return (
        <div className={`app-container ${darkMode ? 'dark-mode' : ''}`}>
            {loading && <Loader />}
            <nav ref={sidebarRef} className={`sidebar ${sidebarOpen ? 'open' : 'collapsed'}`}>
                <div className="sidebar-header">
                    {loading ? (
                        <>
                            <Skeleton height={50} width={50} circle={true} className="slider-header" />
                            <Skeleton height={20} width={100} className="slider-header" />
                        </>
                    ) : (
                        <>
                            <img src={ company.logo_main ? URLZFILES + company.logo_main : ImageNotFound} alt="Logo" className="sidebar-logo" />
                            <span className="sidebar-title">{company.name}</span>
                        </>
                    )}
                    <button className="sidebar-close" onClick={closeSidebar} aria-label="Close sidebar">
                        <i className="bi bi-x"></i>
                    </button>
                </div>
                <div className="sidebar-menu">
                    {loading ? (
                        <Skeleton count={5} height={40} className="slider-header" />
                    ) : (
                        menuItems.map((item, index) => (
                            <React.Fragment key={index}>
                                <MenuItem item={item} index={index} />
                                {index < menuItems.length - 1 && <div className="sidebar-menu-separator" />}
                            </React.Fragment>
                        ))
                    )}
                </div>
            </nav>

            <div className={`main-content ${sidebarOpen ? 'sidebar-open' : 'sidebar-collapsed'}`}>
                <header className="top-bar">
                    <button onClick={toggleSidebar} className="menu-toggle" aria-label="Toggle sidebar">
                        <i className="bi bi-list"></i>
                    </button>
                    <div className="top-bar-right">
                        
                        {/* Mostrar icono de notificaciones solo si shouldShowNotifications() es true */}
                        {shouldShowNotifications() && (
                            <div className="notification-container" style={{ position: 'relative', display: 'inline-block' }}>
                                <button 
                                    onClick={toggleNotificationMenu} 
                                    className="notification-toggle me-2" 
                                    aria-haspopup="true" 
                                    aria-expanded={notificationMenuOpen}
                                >
                                    <i className="bi bi-bell"></i>
                                    {unreadCount > 0 && (
                                        <span className="notification-badge">{unreadCount}</span>
                                    )}
                                </button>

                                {notificationMenuOpen && (
                                    <div ref={notificationRef} className="notification-dropdown">
                                        <div className="notification-header">
                                            <h6>Notificaciones del Sistema</h6>
                                            <button 
                                                className="btn-close btn-close-sm" 
                                                onClick={closeNotificationMenu}
                                            ></button>
                                        </div>
                                        <div className="notification-list">
                                            {notifications.length > 0 ? (
                                                notifications.map((notif, index) => (
                                                    <div 
                                                        key={index} 
                                                        className={`notification-item ${notif.read ? 'read' : 'unread'}`}
                                                        onClick={() => handleNotificationClick(notif)}
                                                        style={{ cursor: 'pointer' }}
                                                    >
                                                        <div className="notification-icon">
                                                            <i className={`bi ${getNotificationIcon(notif)}`}></i>
                                                        </div>
                                                        <div className="notification-content">
                                                            <div className="notification-title">
                                                                {getNotificationTitle(notif)}
                                                                {notif.type === 'policy' && (
                                                                    <span className="badge bg-warning ms-2">Póliza</span>
                                                                )}
                                                                {notif.type === 'kilometrage' && (
                                                                    <span className="badge bg-info ms-2">Kilometraje</span>
                                                                )}
                                                            </div>
                                                            <div className="notification-text">
                                                                {getNotificationText(notif)}
                                                            </div>
                                                            <div className="notification-time">
                                                                {notif.type === 'policy' ? 'Vence: ' : ''}{notif.date}
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))
                                            ) : (
                                                <div className="notification-empty">
                                                    <i className="bi bi-check-circle-fill"></i>
                                                    <p>No hay notificaciones</p>
                                                    <small>Todas las unidades y pólizas están al día</small>
                                                </div>
                                            )}
                                        </div>
                                        <div className="notification-footer">
                                            <div className="d-flex justify-content-between">
                                                <Link to="/kilometrages" className="btn btn-info btn-sm" onClick={closeNotificationMenu}>
                                                    Ver Kilometrajes
                                                </Link>
                                                <Link to="/polizas-seguro" className="btn btn-warning btn-sm" onClick={closeNotificationMenu}>
                                                    Ver Pólizas
                                                </Link>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        <div className="profile-menu" style={{ position: 'relative', display: 'inline-block' }}>
                            <button onClick={toggleProfileMenu} className="profile-toggle" aria-haspopup="true" aria-expanded={profileMenuOpen}>
                                <img src={URLZFILES + avatar} alt="Avatar" className="profile-avatar" />
                                <span className="profile-name">{name + ' ' + last_name}</span>
                                <i className="bi bi-chevron-down profile-icon"></i>
                            </button>
                            {profileMenuOpen && (
                                <div className="profile-dropdown">
                                    <div className="profile-header-card" style={{ textAlign: 'center' }}>
                                        <div className="profile-header-background"></div>
                                        <div className="profile-header-content">
                                            <img src={URLZFILES + avatar} alt="Avatar" className="profile-header-avatar" />
                                            <div className="profile-header-info">
                                                <p className="profile-fullname">{name + ' ' + last_name}</p>
                                                <span className="profile-role-badge">{rol}</span>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="profile-menu-items">
                                        <Link to="/perfil" className="profile-link profile-link-hover" onClick={closeProfileMenu} style={{ textDecoration: 'none' }}>
                                            <i className="bi bi-person-circle"></i>
                                            <span>Mi Perfil</span>
                                        </Link>
                                        <Link to={URLWEB} className="profile-link profile-link-hover" onClick={closeProfileMenu} style={{ textDecoration: 'none' }}>
                                            <i className="bi bi-globe"></i>
                                            <span>Ir a la web</span>
                                        </Link>
                                        <div className="profile-divider"></div>
                                        <a 
                                            className="profile-link profile-link-hover profile-link-danger" 
                                            onClick={() => { closeSession(); closeProfileMenu(); }}
                                            style={{ cursor: "pointer", textDecoration: 'none' }}
                                        >
                                            <i className="bi bi-box-arrow-right"></i>
                                            <span>Cerrar Sesión</span>
                                        </a>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </header>

                <main className="content" ref={contentRef}>
                    <Outlet />
                </main>

                <footer className="footer">
                    <div className="container-fluid d-flex justify-content-between align-items-center">
                        <span className="footer-text">© {year} {company.name}. Todos los derechos reservados.</span>
                        <span className="footer-version">Versión 2.1.0</span>
                    </div>
                </footer>

                {/* Estilos CSS para las notificaciones */}
                <style>
                    {`
                    .notification-toggle {
                        position: relative;
                        background: none;
                        border: none;
                        font-size: 1.25rem;
                        color: var(--text-color);
                        cursor: pointer;
                        padding: 0.5rem;
                        border-radius: 50%;
                        transition: background-color 0.3s;
                    }

                    .notification-toggle:hover {
                        background-color: var(--hover-bg);
                    }

                    .notification-badge {
                        position: absolute;
                        top: 0;
                        right: 0;
                        background-color: #dc3545;
                        color: white;
                        border-radius: 50%;
                        width: 18px;
                        height: 18px;
                        font-size: 0.7rem;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                    }

                    .notification-dropdown {
                        position: absolute;
                        top: 100%;
                        right: 0;
                        width: 400px;
                        background: white;
                        border: 1px solid #dee2e6;
                        border-radius: 0.375rem;
                        box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);
                        z-index: 1000;
                        margin-top: 0.5rem;
                    }

                    .dark-mode .notification-dropdown {
                        background: #2d3748;
                        border-color: #4a5568;
                        color: #e2e8f0;
                    }

                    .notification-header {
                        display: flex;
                        justify-content: space-between;
                        align-items: center;
                        padding: 0.75rem 1rem;
                        border-bottom: 1px solid #dee2e6;
                    }

                    .dark-mode .notification-header {
                        border-bottom-color: #4a5568;
                    }

                    .notification-header h6 {
                        margin: 0;
                        flex: 1;
                    }

                    .notification-list {
                        max-height: 300px;
                        overflow-y: auto;
                    }

                    .notification-item {
                        display: flex;
                        padding: 0.75rem 1rem;
                        border-bottom: 1px solid #f1f3f4;
                        transition: background-color 0.2s;
                    }

                    .dark-mode .notification-item {
                        border-bottom-color: #4a5568;
                    }

                    .notification-item:hover {
                        background-color: #f8f9fa;
                    }

                    .dark-mode .notification-item:hover {
                        background-color: #4a5568;
                    }

                    .notification-item.unread {
                        background-color: #e8f4fd;
                    }

                    .dark-mode .notification-item.unread {
                        background-color: #2c5282;
                    }

                    .notification-icon {
                        margin-right: 0.75rem;
                        font-size: 1.25rem;
                    }

                    .notification-content {
                        flex: 1;
                    }

                    .notification-title {
                        font-weight: 600;
                        margin-bottom: 0.25rem;
                        display: flex;
                        align-items: center;
                    }

                    .notification-text {
                        font-size: 0.875rem;
                        margin-bottom: 0.25rem;
                    }

                    .notification-time {
                        font-size: 0.75rem;
                        color: #6c757d;
                    }

                    .dark-mode .notification-time {
                        color: #a0aec0;
                    }

                    .notification-empty {
                        text-align: center;
                        padding: 2rem 1rem;
                        color: #6c757d;
                    }

                    .dark-mode .notification-empty {
                        color: #a0aec0;
                    }

                    .notification-empty i {
                        font-size: 2rem;
                        margin-bottom: 0.5rem;
                        display: block;
                        color: #28a745;
                    }

                    .notification-footer {
                        padding: 0.75rem 1rem;
                        border-top: 1px solid #dee2e6;
                    }

                    .dark-mode .notification-footer {
                        border-top-color: #4a5568;
                    }

                    .badge.ms-2 {
                        font-size: 0.6rem;
                        padding: 0.2rem 0.4rem;
                    }
                    `}
                </style>
            </div>
        </div>
    );
}

export default MainLayout;